import React, { useEffect, useState } from 'react';
import { Form, Input, message, Select, DatePicker } from 'antd';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import * as accountService from '../../services/account/accountService';
import StandardDrawer from '../../components/common/StandardDrawer';

const AccountCreateForm = ({ visible, onCancel, onSuccess }) => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [employeeType, setEmployeeType] = useState('');
  const [submittable, setSubmittable] = useState(false);
  const values = Form.useWatch([], form);

  useEffect(() => {
    if (visible) {
      form.resetFields();
      setEmployeeType('');
      setSubmittable(false);
    }
  }, [visible, form]);

  // 实时检测必填项是否均已填写（非全空格），控制 Save 按钮是否可点击
  // 其他格式校验（如密码复杂度）在点击 Save 时以错误提示输出
  useEffect(() => {
    if (!visible) return;
    const isFilled = (v) => typeof v === 'string' ? v.trim().length > 0 : v != null && v !== '';
    const requiredOk =
      isFilled(values?.loginId) &&
      isFilled(values?.password) &&
      isFilled(values?.name) &&
      isFilled(values?.employeeType) &&
      (values?.employeeType !== 'Dummy' || isFilled(values?.expiryDate));
    setSubmittable(requiredOk);
  }, [values, visible]);

  const handleEmployeeTypeChange = (value) => {
    setEmployeeType(value);
    if (value !== 'Dummy') {
      form.setFieldValue('expiryDate', null);
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();
      
      // 构造API请求数据
      const { expiryDate, ...rest } = values;
      const accountData = {
        ...rest,
        expireTime: expiryDate ? dayjs(expiryDate).format('YYYY-MM-DD') : null,
        passwordType: "0",
        sourceCode: values.employeeType === 'Formal' ? 1 : 2
      };
      
      const response = await accountService.createAccount(accountData);
      
      if (response.success) {
        message.success(t('account.createSuccess'));
        onSuccess();
      } else {
        message.error(response.errorMessage || t('account.createFailed'));
      }
    } catch (error) {
      console.error('Create account failed:', error);
      message.error(error.message || t('account.createFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <StandardDrawer
      visible={visible}
      onClose={onCancel}
      onOk={handleSubmit}
      title={t('account.createTitle')}
      confirmLoading={loading}
      width={480}
      maskClosable={false}
      destroyOnClose={true}
      okDisabled={!submittable}
    >
      <Form
        form={form}
        layout="vertical"
        style={{ padding: '24px 0' }}
      >
        <Form.Item
          name="loginId"
          label={<span className="required-label">{t('account.email')}</span>}
          rules={[
            { required: true, message: t('account.emailInvalid') },
            { type: 'email', message: t('account.emailInvalid') }
          ]}
        >
          <Input placeholder={t('common.pleaseInput')} />
        </Form.Item>

        <Form.Item
          name="password"
          label={<span className="required-label">{t('account.password')}</span>}
          rules={[
            { required: true, message: t('account.passwordRequired') },
            { min: 8, max: 20, message: t('account.passwordLengthRequired') },
            {
              pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d]).{8,20}$/,
              message: t('account.passwordComplexityRequired')
            }
          ]}
        >
          <Input.Password placeholder={t('common.pleaseInput')} maxLength={20} />
        </Form.Item>

        <Form.Item
          name="name"
          label={<span className="required-label">{t('account.name')}</span>}
          rules={[
            { required: true, whitespace: true, message: t('account.nameRequired') },
            {
              pattern: /^[^!@#$%^&*()]+$/,
              message: t('account.nameSpecialCharNotAllowed') || '名字不能包含特殊字符 !@#$%^&*()'
            }
          ]}
        >
          <Input placeholder={t('common.pleaseInput')} />
        </Form.Item>

        <Form.Item
          name="mobile"
          label={t('account.contactPhone')}
        >
          <Input placeholder={t('common.pleaseInput')} />
        </Form.Item>

        <Form.Item
          name="employeeType"
          label={<span className="required-label">{t('account.employeeType')}</span>}
          rules={[{ required: true, message: t('account.employeeTypeRequired') }]}
        >
          <Select
            placeholder={t('common.pleaseSelect')}
            options={[
              { value: 'Dummy', label: t('account.employeeTypeDummy') }
            ]}
            onChange={handleEmployeeTypeChange}
          />
        </Form.Item>

        {employeeType === 'Dummy' && (
          <Form.Item
            name="expiryDate"
            label={<span className="required-label">{t('account.expiryDate')}</span>}
            rules={[{ required: true, message: t('account.expiryDateRequired') }]}
          >
            <DatePicker
              style={{ width: '100%' }}
              placeholder={t('common.pleaseSelect')}
            />
          </Form.Item>
        )}

        {/* <Form.Item
          name="mobile"
          label={t('account.mobile')}
        >
          <Input placeholder={t('common.pleaseInput')} />
        </Form.Item> */}
      </Form>
    </StandardDrawer>
  );
};

export default AccountCreateForm;